'use client';

import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { DeleteTeamButton } from './DeleteTeamButton';
import { PlayerCard } from './PlayerCard';
import type { Team, Gender, Player } from '@/types';
import { getGenderLabel } from '@/utils/gender-utils';
import type { CategoryChangeAction } from '@/hooks/useCategoryChange';
import { EloCategory } from '../../models/elo.model';
import { ELO_RATING_BADGE_CLASSNAME, formatEloBadgeLabel } from '../../utils';
import type { Availability } from '@/types/tournament.model';
import { AvailabilitySection } from './AvailabilitySection';

interface TeamCardProps {
  team: Team & { originalIndex?: number };
  index: number;
  sportUid: string;
  isExpanded: boolean;
  eloCategories: EloCategory[];
  availability?: Availability[];
  tournamentSchedule?: Availability[];
  toggleTeam: () => void;
  getTeamAverageElo: (team: Team) => number;
  getTeamGender: (team: Team) => Gender | null;
  getPlayerInitials: (name: string) => string;
  formatWhatsAppNumber: (phone: string) => string;
  onDelete?: () => void;
  canDelete?: boolean;
  /** If present, enables replacing players on this team. */
  onReplacePlayer?: (player: Player) => void;
  /** If present, enables editing the team's availability. */
  onEditAvailability?: () => void;
  /** If present, offers moving each player to another category. */
  getCategoryChange?: (player: Player) => CategoryChangeAction | undefined;
}

export function TeamCard({
  team,
  index,
  sportUid,
  isExpanded,
  eloCategories,
  availability = [],
  tournamentSchedule,
  toggleTeam,
  getTeamAverageElo,
  getTeamGender,
  getPlayerInitials,
  formatWhatsAppNumber,
  onDelete,
  canDelete,
  onReplacePlayer,
  onEditAvailability,
  getCategoryChange,
}: TeamCardProps) {
  const teamElo = getTeamAverageElo(team);
  const teamGender = getTeamGender(team);

  return (
    <div className="overflow-hidden rounded-lg border">
      <div
        className="flex cursor-pointer flex-wrap items-center justify-between bg-gray-50 p-4"
        onClick={toggleTeam}
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {isExpanded ? (
            <ChevronDown className="h-5 w-5 shrink-0 text-gray-500" />
          ) : (
            <ChevronRight className="h-5 w-5 shrink-0 text-gray-500" />
          )}
          <div className="flex min-w-0 flex-col gap-1">
            <h3 className="whitespace-nowrap font-semibold">
              Equipo #{team.originalIndex || index + 1}
            </h3>
            {(teamElo > 0 || teamGender) && (
              <div className="flex flex-wrap items-center gap-1.5">
                {teamElo > 0 && (
                  <Badge className={ELO_RATING_BADGE_CLASSNAME}>
                    {formatEloBadgeLabel(teamElo, eloCategories)}
                  </Badge>
                )}
                {teamGender && (
                  <Badge variant="outline" className="shrink-0">
                    {getGenderLabel(teamGender as Gender)}
                  </Badge>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Capped so long names wrap here instead of squeezing the team's column and its badges. */}
        <div className="hidden items-center gap-2 sm:ml-4 sm:flex sm:max-w-[50%] sm:flex-wrap">
          <span className="mr-1 text-sm text-gray-500">Jugadores:</span>
          {team.players.map((player, playerIndex) => (
            <span key={playerIndex} className="text-sm font-medium">
              {player.name}
              {playerIndex < team.players.length - 1 ? ',' : ''}
            </span>
          ))}
        </div>

        <DeleteTeamButton canDelete={canDelete} onDelete={onDelete} />
      </div>

      {isExpanded && (
        <div className="border-t border-gray-200 p-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {team.players.map((player, playerIndex) => (
              <PlayerCard
                key={player.id || `player-${playerIndex}`}
                player={player}
                playerIndex={playerIndex}
                sportUid={sportUid}
                eloCategories={eloCategories}
                getPlayerInitials={getPlayerInitials}
                formatWhatsAppNumber={formatWhatsAppNumber}
                onReplace={
                  onReplacePlayer ? () => onReplacePlayer(player) : undefined
                }
                categoryChange={getCategoryChange?.(player)}
              />
            ))}
          </div>

          {/* Availability */}
          <AvailabilitySection
            availability={availability}
            schedule={tournamentSchedule}
            className="mt-6"
            onEdit={onEditAvailability}
          />
        </div>
      )}
    </div>
  );
}
